
import Button from '../../components/base/Button';
import { useEffect } from 'react';

export default function About() {
  // Add Schema.org JSON-LD for About page
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Über Dion Bamme",
      "description": "Erfahren Sie mehr über Dion Bamme, einen leidenschaftlichen Marketing Management Student aus Wernigerode",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/about`,
      "mainEntity": {
        "@type": "Person",
        "name": "Dion Bamme",
        "jobTitle": "Marketing Management Student",
        "description": "18-jähriger Marketing Management Student mit dem Traum, die Welt durch kreative Kampagnen zu verändern",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Wernigerode",
          "addressCountry": "DE"
        },
        "alumniOf": [
          {
            "@type": "EducationalOrganization",
            "name": "Hochschule Harz",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Wernigerode",
              "addressCountry": "DE"
            }
          },
          {
            "@type": "EducationalOrganization",
            "name": "Landschulheim Grovesmühle",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Veckenstedt",
              "addressCountry": "DE"
            }
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-pink-400/20 to-yellow-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-6xl font-black mb-6">
                Über <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">mich</span>
              </h1>
              <p className="text-2xl text-gray-600 mb-8 leading-relaxed">
                Ein leidenschaftlicher Marketing Student mit dem Traum, 
                die Welt durch kreative Kampagnen zu verändern.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full font-medium">18 Jahre</span>
                <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full font-medium">1. Semester</span>
                <span className="px-4 py-2 bg-pink-100 text-pink-700 rounded-full font-medium">Wernigerode</span>
                <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full font-medium">Marketing Management</span>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-3xl overflow-hidden bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 relative shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/3c6bb67e5f3907f7974351c0796fa0f0.jpg"
                  alt="Dion Bamme"
                  className="w-full h-full object-cover object-top"
                />
                {/* Floating Elements */}
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl flex items-center justify-center transform rotate-12 shadow-lg">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-star-fill text-white text-xl"></i>
                  </div>
                </div>
                <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-r from-green-400 to-blue-400 rounded-full flex items-center justify-center transform -rotate-12 shadow-lg">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-heart-fill text-white text-2xl"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-black mb-12 text-center">
              Meine <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Geschichte</span>
            </h2>
            
            <div className="space-y-8">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-3xl border border-blue-100">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Der Anfang</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Schon in der Schule war ich fasziniert von kreativen Werbekampagnen. Während andere 
                  Werbung übersprungen haben, habe ich sie studiert. Wie schaffen es Marken, Emotionen 
                  zu wecken? Wie entstehen virale Trends? Diese Fragen haben mich schon früh beschäftigt.
                </p>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-3xl border border-purple-100">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Die Entscheidung</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Nach dem Abitur war klar: Marketing Management ist mein Weg. Nicht nur, weil es 
                  spannend ist, sondern weil ich glaube, dass gutes Marketing die Welt verbessern kann. 
                  Es geht darum, die richtigen Menschen mit den richtigen Botschaften zu erreichen.
                </p>
              </div>

              <div className="bg-gradient-to-r from-pink-50 to-yellow-50 p-8 rounded-3xl border border-pink-100">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Die Vision</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Mein Traum ist es, eine Marketingagentur zu gründen, die sich auf junge, innovative 
                  Unternehmen spezialisiert. Ich möchte Marken dabei helfen, authentisch zu kommunizieren 
                  und echte Verbindungen zu ihren Kunden aufzubauen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education & Experience */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black mb-16 text-center">
            Bildung & <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Erfahrung</span>
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Education */}
            <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mr-4">
                  <i className="ri-graduation-cap-line text-white text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Bildungsweg</h3>
              </div>
              
              <div className="space-y-6">
                <div className="border-l-4 border-blue-500 pl-6">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-blue-600 bg-blue-100 px-3 py-1 rounded-full">Aktuell</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Marketing Management (B.A.)</h4>
                  <p className="text-gray-600">1. Semester • Hochschule Harz, Wernigerode</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Schwerpunkte: Digitales Marketing, Marktforschung, Konsumentenverhalten
                  </p>
                </div>

                <div className="border-l-4 border-gray-300 pl-6">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">2024</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Abitur</h4>
                  <p className="text-gray-600">Landschulheim Grovesmühle, Veckenstedt</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Klasse 10-12 • Abiturnote: 2,0
                  </p>
                </div>

                <div className="border-l-4 border-gray-300 pl-6">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">2021-2024</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Landschulheim Grovesmühle</h4>
                  <p className="text-gray-600">Veckenstedt</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Klasse 10-12 • Abitur mit Note 2,0
                  </p>
                </div>

                <div className="border-l-4 border-gray-300 pl-6">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">2018-2021</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Landesgymnasium für Musik</h4>
                  <p className="text-gray-600">Wernigerode</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Klasse 5-10 • Musikalische Ausbildung
                  </p>
                </div>
              </div>
            </div>

            {/* Experience */}
            <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mr-4">
                  <i className="ri-briefcase-line text-white text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Erfahrungen</h3>
              </div>
              
              <div className="space-y-6">
                <div className="border-l-4 border-green-500 pl-6">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-green-600 bg-green-100 px-3 py-1 rounded-full">Freelance</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Webdesign für kleine Unternehmen</h4>
                  <p className="text-gray-600">2025 - heute • Verschiedene Kunden</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Responsive Websites, E-Commerce Lösungen, Landing Pages
                  </p>
                </div>

                <div className="border-l-4 border-purple-500 pl-6">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-purple-600 bg-purple-100 px-3 py-1 rounded-full">Minijob</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Gastronomie</h4>
                  <p className="text-gray-600">Restaurant XO & Café Nachtigall • 2023 • 4 Monate</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Service, Kundenbetreuung, Teamarbeit in der Gastronomie
                  </p>
                </div>
                
                <div className="border-l-4 border-yellow-500 pl-6">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-yellow-600 bg-yellow-100 px-3 py-1 rounded-full">Praktikum</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Chocotech GmbH</h4>
                  <p className="text-gray-600">Wernigerode • 2023 • 1 Woche</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Einblick in Produktionsprozesse und Unternehmensabläufe
                  </p>
                </div>
                
                <div className="border-l-4 border-indigo-500 pl-6">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-indigo-600 bg-indigo-100 px-3 py-1 rounded-full">Praktikum</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Polizei Wernigerode</h4>
                  <p className="text-gray-600">Wernigerode • 2023 • 1 Woche</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Einblick in Polizeiarbeit und öffentliche Sicherheit
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills & Interests */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black mb-16 text-center">
            Skills & <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Interessen</span>
          </h2>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Technical Skills */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-3xl border border-blue-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mr-4">
                  <i className="ri-code-line text-white text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Technical Skills</h3>
              </div>
              <div className="space-y-4">
                {[
                  { name: 'Adobe Creative Suite', level: 75 },
                  { name: 'Google Analytics', level: 60 },
                  { name: 'Social Media Tools', level: 85 },
                  { name: 'Excel & PowerPoint', level: 90 }
                ].map((skill, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{skill.name}</span>
                      <span className="text-sm text-gray-500">{skill.level}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-1000"
                        style={{width: `${skill.level}%`}}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Soft Skills */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-3xl border border-green-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mr-4">
                  <i className="ri-team-line text-white text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Soft Skills</h3>
              </div>
              <div className="space-y-3">
                {[
                  'Kreativität & Innovation',
                  'Teamarbeit & Kommunikation',
                  'Analytisches Denken',
                  'Projektmanagement',
                  'Präsentationsfähigkeiten',
                  'Zeitmanagement'
                ].map((skill, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-full mr-3"></div>
                    <span className="text-gray-700">{skill}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Interests */}
            <div className="bg-gradient-to-br from-pink-50 to-yellow-50 p-8 rounded-3xl border border-pink-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-yellow-500 rounded-2xl flex items-center justify-center mr-4">
                  <i className="ri-heart-line text-white text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Interessen</h3>
              </div>
              <div className="space-y-4">
                {[
                  { icon: 'ri-camera-line', name: 'Fotografie', desc: 'Street & Portrait' },
                  { icon: 'ri-music-line', name: 'Musik', desc: 'Hip-Hop & Electronic' },
                  { icon: 'ri-gamepad-line', name: 'Gaming', desc: 'Strategy & Indie Games' },
                  { icon: 'ri-book-line', name: 'Lesen', desc: 'Marketing & Psychologie' },
                  { icon: 'ri-plane-line', name: 'Reisen', desc: 'Neue Kulturen entdecken' }
                ].map((interest, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-yellow-500 rounded-lg flex items-center justify-center mr-3">
                      <i className={`${interest.icon} text-white text-sm`}></i>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{interest.name}</div>
                      <div className="text-sm text-gray-600">{interest.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Goals Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black mb-16 text-center">
            Meine <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Ziele</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-trophy-line',
                title: 'Studium mit Auszeichnung',
                desc: 'Top 10% des Jahrgangs mit praktischer Erfahrung kombinieren',
                color: 'from-yellow-400 to-orange-400',
                bgColor: 'from-yellow-50 to-orange-50'
              },
              {
                icon: 'ri-rocket-line',
                title: 'Eigene Agentur gründen',
                desc: 'Innovative Marketinglösungen für Startups und junge Unternehmen',
                color: 'from-blue-400 to-purple-400',
                bgColor: 'from-blue-50 to-purple-50'
              },
              {
                icon: 'ri-global-line',
                title: 'International arbeiten',
                desc: 'Erfahrungen in verschiedenen Märkten und Kulturen sammeln',
                color: 'from-green-400 to-teal-400',
                bgColor: 'from-green-50 to-teal-50'
              }
            ].map((goal, index) => (
              <div key={index} className={`bg-gradient-to-br ${goal.bgColor} p-8 rounded-3xl border border-gray-100 text-center hover:transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl`}>
                <div className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-r ${goal.color} rounded-3xl flex items-center justify-center shadow-lg`}>
                  <i className={`${goal.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">{goal.title}</h3>
                <p className="text-gray-600 leading-relaxed">{goal.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personal Quote */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <i className="ri-double-quotes-l text-6xl text-white/30"></i>
          </div>
          <blockquote className="text-3xl font-bold text-white mb-8 leading-relaxed">
            "Marketing ist nicht nur Werbung – es ist die Kunst, 
            echte Verbindungen zwischen Menschen und Marken zu schaffen."
          </blockquote>
          <cite className="text-xl text-white/80">— Dion Bamme</cite>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black mb-6 text-gray-900">
            Lassen Sie uns <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">zusammenarbeiten</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Haben Sie ein spannendes Projekt oder möchten Sie mehr über 
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold py-3 px-6 rounded-lg"
            >
              Jetzt Kontaktieren
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
